/**
 * Revenue Analytics Page - Umsatzanalysen und Berichte
 * Mobile-optimiert mit responsivem Design
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Container,
  Collapse,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Analytics as AnalyticsIcon,
  PictureAsPdf as PdfIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/de";
import {
  RevenueStats,
  RevenueFilters,
  InvoiceStatus,
  RevenueTrends,
} from "../types/api-types";
import { apiClient } from "../services/apiClient";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const RevenueAnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Set dayjs locale to German
  dayjs.locale("de");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueStats | null>(null);
  const [trendsData, setTrendsData] = useState<RevenueTrends | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(!isMobile);

  // Filter State
  const [filters, setFilters] = useState<RevenueFilters>({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    groupBy: "day",
  });

  // Temporary date values for datepickers (using Dayjs for MUI DatePicker)
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(
    dayjs(getDefaultStartDate())
  );
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(
    dayjs(getDefaultEndDate())
  );

  // Format period based on groupBy
  const formatPeriod = (period: string, groupBy: string): string => {
    switch (groupBy) {
      case "day":
        const date = new Date(period);
        return date.toLocaleDateString("de-DE");
      case "week":
        // Handle formats like "2023-W35" or "2023-35"
        let year: string, week: string;
        if (period.includes("-W")) {
          [year, week] = period.split("-W");
        } else {
          [year, week] = period.split("-");
        }
        return `KW ${week} ${year}`;
      case "month":
        const [y, m] = period.split("-");
        const monthNames = [
          "Januar",
          "Februar",
          "März",
          "April",
          "Mai",
          "Juni",
          "Juli",
          "August",
          "September",
          "Oktober",
          "November",
          "Dezember",
        ];
        return `${monthNames[parseInt(m) - 1]} ${y}`;
      case "quarter":
        const [yr, q] = period.split("-Q");
        return `Q${q} ${yr}`;
      case "year":
        return period;
      default:
        return period;
    }
  };

  // Formatted data for display
  const formattedRevenueByPeriod = useMemo(() => {
    if (!revenueData) return [];
    return revenueData.revenueByPeriod.map((item) => ({
      ...item,
      period: formatPeriod(item.period, filters.groupBy || "day"),
      previousAmount: item.previousAmount || 0,
      growth: item.growth || 0,
      growthPercentage: item.growthPercentage || 0,
    }));
  }, [revenueData, filters.groupBy]);

  // Default date range (last 30 days)
  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split("T")[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split("T")[0];
  }

  // Load revenue data
  const loadRevenueData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getRevenueAnalytics(filters);
      setRevenueData(data);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Fehler beim Laden der Umsatzanalyse"
      );
    } finally {
      setLoading(false);
    }
  };

  // Load trends data
  const loadTrendsData = async () => {
    if (!filters.startDate || !filters.endDate) return;

    try {
      const trendsResult = await apiClient.getRevenueTrends(
        filters.startDate,
        filters.endDate,
        filters.groupBy
      );
      setTrendsData(trendsResult);
    } catch (err: any) {
      console.error("Fehler beim Laden der Trend-Daten:", err);
      // Trends are optional, don't show error for this
    }
  };

  // Load data on component mount and filter changes
  useEffect(() => {
    const loadData = async () => {
      await loadRevenueData();
      await loadTrendsData();
    };
    loadData();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize temporary date values
  useEffect(() => {
    setTempStartDate(dayjs(filters.startDate || getDefaultStartDate()));
    setTempEndDate(dayjs(filters.endDate || getDefaultEndDate()));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes
  const handleFilterChange = (key: keyof RevenueFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Reset filters
  const resetFilters = () => {
    const defaultStart = getDefaultStartDate();
    const defaultEnd = getDefaultEndDate();
    setFilters({
      startDate: defaultStart,
      endDate: defaultEnd,
      groupBy: "day",
    });
    setTempStartDate(dayjs(defaultStart));
    setTempEndDate(dayjs(defaultEnd));
  };

  // PDF Export
  const exportToPDF = async () => {
    try {
      setExportLoading(true);

      if (!revenueData) {
        throw new Error("Keine Daten zum Exportieren verfügbar");
      }

      const pdf = new jsPDF();
      let currentY = 20;

      // Header
      pdf.setFontSize(20);
      pdf.text("Umsatzanalyse", 20, currentY);
      currentY += 15;

      pdf.setFontSize(12);
      const startDateStr = filters.startDate
        ? new Date(filters.startDate).toLocaleDateString("de-DE")
        : "Alle";
      const endDateStr = filters.endDate
        ? new Date(filters.endDate).toLocaleDateString("de-DE")
        : "Alle";
      pdf.text(`Zeitraum: ${startDateStr} - ${endDateStr}`, 20, currentY);
      currentY += 10;

      const groupByLabels: Record<string, string> = {
        day: "Tag",
        week: "Woche",
        month: "Monat",
        quarter: "Quartal",
        year: "Jahr",
      };

      const currentGroupBy = filters.groupBy || "day";
      const groupByLabel = groupByLabels[currentGroupBy] || "Tag";

      pdf.text(`Gruppierung: ${groupByLabel}`, 20, currentY);
      currentY += 10;

      pdf.text(
        `Erstellt am: ${new Date().toLocaleDateString("de-DE")}`,
        20,
        currentY
      );
      currentY += 20;

      // Summary
      pdf.setFontSize(16);
      pdf.text("Zusammenfassung", 20, currentY);
      currentY += 10;

      const summaryData = [
        [
          "Gesamtumsatz",
          `${revenueData.totalRevenue.toLocaleString("de-DE", {
            style: "currency",
            currency: "EUR",
          })}`,
        ],
        ["Anzahl Rechnungen", revenueData.totalInvoices.toString()],
        [
          "Durchschnittlicher Rechnungsbetrag",
          `${revenueData.averageInvoiceAmount.toLocaleString("de-DE", {
            style: "currency",
            currency: "EUR",
          })}`,
        ],
      ];

      autoTable(pdf, {
        head: [["Metrik", "Wert"]],
        body: summaryData,
        startY: currentY,
        margin: { left: 20, right: 20 },
        theme: "grid",
      });

      currentY = (pdf as any).lastAutoTable?.finalY || currentY + 80;

      // Revenue by Period Table
      if (revenueData.revenueByPeriod.length > 0) {
        currentY += 20;

        pdf.setFontSize(16);
        pdf.text(`Umsatzentwicklung nach ${groupByLabel}`, 20, currentY);
        currentY += 10;

        const periodData = revenueData.revenueByPeriod.map((period) => [
          formatPeriod(period.period, currentGroupBy),
          period.count.toString(),
          `${period.totalAmount.toLocaleString("de-DE", {
            style: "currency",
            currency: "EUR",
          })}`,
          `${(period.previousAmount || 0).toLocaleString("de-DE", {
            style: "currency",
            currency: "EUR",
          })}`,
          `${(period.growth || 0).toLocaleString("de-DE", {
            style: "currency",
            currency: "EUR",
          })}`,
          `${
            period.growthPercentage ? period.growthPercentage.toFixed(1) : "0.0"
          }%`,
        ]);

        autoTable(pdf, {
          head: [
            [
              groupByLabel,
              "Anzahl",
              "Aktuell",
              "Vorjahr",
              "Wachstum",
              "Wachstum %",
            ],
          ],
          body: periodData,
          startY: currentY,
          margin: { left: 20, right: 20 },
          theme: "grid",
          styles: {
            fontSize: 9,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [100, 100, 100],
            textColor: 255,
            fontStyle: "bold",
          },
          columnStyles: {
            0: { cellWidth: 40 }, // Period
            1: { cellWidth: 20, halign: "right" }, // Count
            2: { cellWidth: 30, halign: "right" }, // Total Amount (Aktuell)
            3: { cellWidth: 30, halign: "right" }, // Previous Amount (Vorjahr)
            4: { cellWidth: 30, halign: "right" }, // Growth (Wachstum)
            5: { cellWidth: 20, halign: "right" }, // Growth Percentage (Wachstum %)
          },
        });

        currentY = (pdf as any).lastAutoTable?.finalY || currentY + 100;
      }

      // Save PDF
      const filename = `umsatzanalyse_${groupByLabel}_${filters.startDate}_${filters.endDate}.pdf`;
      pdf.save(filename);
    } catch (err: any) {
      setError(
        "Fehler beim PDF-Export: " + (err.message || "Unbekannter Fehler")
      );
    } finally {
      setExportLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(1)}%`;
  };

  // Status colors
  const statusColors = {
    [InvoiceStatus.DRAFT]: "#9e9e9e",
    [InvoiceStatus.PAID]: "#4caf50",
  };

  // Status labels
  const getStatusLabel = (status: InvoiceStatus) => {
    const labels = {
      [InvoiceStatus.DRAFT]: "Entwurf",
      [InvoiceStatus.PAID]: "Bezahlt",
    };
    return labels[status as keyof typeof labels] || status;
  };

  if (loading) {
    return (
      <Container
        maxWidth={false}
        disableGutters={isMobile}
        sx={{
          py: { xs: 0, md: 3 },
          px: { xs: 1, md: 3 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Lade Umsatzdaten...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth={false}
        disableGutters={isMobile}
        sx={{
          py: { xs: 0, md: 3 },
          px: { xs: 1, md: 3 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button onClick={loadRevenueData} sx={{ ml: 2 }}>
            Erneut versuchen
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!revenueData) {
    return (
      <Container
        maxWidth={false}
        disableGutters={isMobile}
        sx={{
          py: { xs: 0, md: 3 },
          px: { xs: 1, md: 3 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Alert severity="warning">Keine Umsatzdaten verfügbar.</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      disableGutters={isMobile}
      sx={{
        py: { xs: 0, md: 3 },
        px: { xs: 1, md: 3 },
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={2}
        >
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1">
              <AnalyticsIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Umsatzanalyse
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1}
            flexDirection={{ xs: "column", sm: "row" }}
            width={{ xs: "100%", sm: "auto" }}
          >
            {isMobile && (
              <Button
                variant="outlined"
                startIcon={
                  showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
                onClick={() => setShowFilters(!showFilters)}
                fullWidth
                size="large"
              >
                Filter {showFilters ? "ausblenden" : "anzeigen"}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadRevenueData}
              disabled={loading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
            >
              Aktualisieren
            </Button>
            <Button
              variant="contained"
              startIcon={
                exportLoading ? <CircularProgress size={20} /> : <PdfIcon />
              }
              onClick={exportToPDF}
              disabled={exportLoading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
            >
              {exportLoading ? "Exportiere..." : "PDF Export"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Filter Section */}
      <Collapse in={!isMobile || showFilters}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
          <Card sx={{ mb: { xs: 2, md: 3 } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filter
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Von"
                    value={tempStartDate}
                    onChange={(newValue) => {
                      setTempStartDate(newValue);
                      if (newValue) {
                        handleFilterChange(
                          "startDate",
                          newValue.format("YYYY-MM-DD")
                        );
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: isMobile ? "medium" : "small",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Bis"
                    value={tempEndDate}
                    onChange={(newValue) => {
                      setTempEndDate(newValue);
                      if (newValue) {
                        handleFilterChange(
                          "endDate",
                          newValue.format("YYYY-MM-DD")
                        );
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: isMobile ? "medium" : "small",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                    <InputLabel>Gruppierung</InputLabel>
                    <Select
                      value={filters.groupBy || "day"}
                      label="Gruppierung"
                      onChange={(e) =>
                        handleFilterChange("groupBy", e.target.value)
                      }
                    >
                      <MenuItem value="day">Tag</MenuItem>
                      <MenuItem value="week">Woche</MenuItem>
                      <MenuItem value="month">Monat</MenuItem>
                      <MenuItem value="quarter">Quartal</MenuItem>
                      <MenuItem value="year">Jahr</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    onClick={resetFilters}
                    variant="outlined"
                    fullWidth
                    size={isMobile ? "large" : "medium"}
                  >
                    Filter zurücksetzen
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </LocalizationProvider>
      </Collapse>

      {/* Period Comparison Cards */}
      {trendsData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent
                sx={{
                  textAlign: "center",
                  p: { xs: 1.5, md: 2 },
                  "&:last-child": { pb: { xs: 1.5, md: 2 } },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
                >
                  Umsatzänderung
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      trendsData.comparison.revenueGrowth >= 0
                        ? "success.main"
                        : "error.main",
                    fontSize: { xs: "0.9rem", md: "1.25rem" },
                    fontWeight: 600,
                  }}
                >
                  {formatCurrency(trendsData.comparison.revenueGrowth)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      trendsData.comparison.revenueGrowthPercentage >= 0
                        ? "success.main"
                        : "error.main",
                    fontSize: { xs: "0.7rem", md: "0.875rem" },
                  }}
                >
                  {trendsData.comparison.revenueGrowthPercentage >= 0
                    ? "+"
                    : ""}
                  {trendsData.comparison.revenueGrowthPercentage.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent
                sx={{
                  textAlign: "center",
                  p: { xs: 1.5, md: 2 },
                  "&:last-child": { pb: { xs: 1.5, md: 2 } },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
                >
                  Rechnungsänderung
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      trendsData.comparison.invoiceGrowth >= 0
                        ? "success.main"
                        : "error.main",
                    fontSize: { xs: "0.9rem", md: "1.25rem" },
                    fontWeight: 600,
                  }}
                >
                  {trendsData.comparison.invoiceGrowth >= 0 ? "+" : ""}
                  {trendsData.comparison.invoiceGrowth}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}
                >
                  Anzahl
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent
                sx={{
                  textAlign: "center",
                  p: { xs: 1.5, md: 2 },
                  "&:last-child": { pb: { xs: 1.5, md: 2 } },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
                >
                  Aktuelle Periode
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "0.9rem", md: "1.25rem" },
                    fontWeight: 600,
                  }}
                >
                  {formatCurrency(trendsData.current.totalRevenue)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}
                >
                  {trendsData.current.totalInvoices} Rechnungen
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={3}>
            <Card>
              <CardContent
                sx={{
                  textAlign: "center",
                  p: { xs: 1.5, md: 2 },
                  "&:last-child": { pb: { xs: 1.5, md: 2 } },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
                >
                  Vorherige Periode
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "0.9rem", md: "1.25rem" },
                    fontWeight: 600,
                  }}
                >
                  {formatCurrency(trendsData.previous.totalRevenue)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}
                >
                  {trendsData.previous.totalInvoices} Rechnungen
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Key Metrics - Responsive Grid */}
      <Grid container spacing={{ xs: 1, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={4} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent
              sx={{
                textAlign: "center",
                p: { xs: 1, md: 3 },
                "&:last-child": { pb: { xs: 1, md: 3 } },
              }}
            >
              <MoneyIcon
                color="primary"
                sx={{
                  fontSize: { xs: 20, md: 40 },
                  mb: { xs: 0.5, md: 1 },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
              >
                Umsatz
              </Typography>
              <Typography
                variant={isMobile ? "body1" : "h5"}
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", md: "1.5rem" },
                  lineHeight: 1.2,
                }}
              >
                {formatCurrency(revenueData.totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent
              sx={{
                textAlign: "center",
                p: { xs: 1, md: 3 },
                "&:last-child": { pb: { xs: 1, md: 3 } },
              }}
            >
              <ReceiptIcon
                color="primary"
                sx={{
                  fontSize: { xs: 20, md: 40 },
                  mb: { xs: 0.5, md: 1 },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
              >
                Rechnungen
              </Typography>
              <Typography
                variant={isMobile ? "body1" : "h5"}
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", md: "1.5rem" },
                  lineHeight: 1.2,
                }}
              >
                {revenueData.totalInvoices}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent
              sx={{
                textAlign: "center",
                p: { xs: 1, md: 3 },
                "&:last-child": { pb: { xs: 1, md: 3 } },
              }}
            >
              <AnalyticsIcon
                color="primary"
                sx={{
                  fontSize: { xs: 20, md: 40 },
                  mb: { xs: 0.5, md: 1 },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.65rem", md: "0.875rem" } }}
              >
                Ø Betrag
              </Typography>
              <Typography
                variant={isMobile ? "body1" : "h5"}
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", md: "1.5rem" },
                  lineHeight: 1.2,
                }}
              >
                {formatCurrency(revenueData.averageInvoiceAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Chart */}
      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              width: "100%",
              height: { xs: 300, md: 400 },
              overflowX: "visible",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={formattedRevenueByPeriod}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  fontSize={isMobile ? 10 : 12}
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? "end" : "middle"}
                  height={isMobile ? 60 : 30}
                />
                <YAxis
                  tickFormatter={(value) => formatCurrency(value)}
                  fontSize={isMobile ? 10 : 12}
                />
                <RechartsTooltip
                  formatter={(value: any, name: string) => [
                    formatCurrency(Number(value)),
                    name === "totalAmount" ? "Aktuell" : "Vorjahr",
                  ]}
                />
                <Legend />
                <Bar dataKey="totalAmount" fill="#667eea" name="Aktuell" />
                <Bar dataKey="previousAmount" fill="#764ba2" name="Vorjahr" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default RevenueAnalyticsPage;
